import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as datetime from 'legacy/common/utils/datetime';
import { error as toastError } from 'legacy/common/utils/toast';
import SearchBar from './components/SearchBar';
import DirectoryLetters from './components/DirectoryLetters';
import OrgCard from './components/OrgCard';

import { useAssociationGroupsInfinite } from 'api/queries/associations';
import type { AssociationGroupDto } from 'api/dtos/associations';
import OrgDirectorySkeleton from './components/skeletons/OrgDirectorySkeleton';
import OrgDirectoryGridSkeleton from './components/skeletons/OrgDirectoryGridSkeleton';

interface OrgDirectoryProps {
  associationId: string;
  organizations?: AssociationGroupDto[];
  limit?: number;
  user?: any;
}

const OrgDirectory: React.FC<OrgDirectoryProps> = ({
  associationId,
  organizations: overrideOrganizations,
  limit = 12,
  user
}) => {
  const { t } = useTranslation(['organizations']);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('');
  
  // Check if user is authenticated
  const isAuthenticated = !!user;
  
  // Track if this is the very first load (not filtering/searching)
  const isInitialLoad = useRef(true);
  const hasLoadedOnce = useRef(false);

  // Use unified API - includes userMembership if user is authenticated
  const {
    data: infiniteData,
    isLoading: isLoadingGroups,
    error: groupsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useAssociationGroupsInfinite({
    associationId,
    query: searchTerm || undefined,
    letter: selectedLetter || undefined,
    limit,
    enabled: !overrideOrganizations,
    useUnauthenticatedEndpoint: !isAuthenticated
  });

  const organizations: AssociationGroupDto[] =
    overrideOrganizations ||
    infiniteData?.pages.flatMap((page) => page.items) ||
    [];
  const availableLetters = infiniteData?.pages[0]?.availableLetters || [];
  // Both authenticated and public APIs now have orgsCount
  const totalOrgsCount = infiniteData?.pages[0]?.orgsCount || 0;
  
  // Detect if we're on the initial load vs filtering
  useEffect(() => {
    if (!isLoadingGroups && infiniteData) {
      hasLoadedOnce.current = true;
    }
  }, [isLoadingGroups, infiniteData]);

  useEffect(() => {
    if (searchTerm || selectedLetter) {
      isInitialLoad.current = false;
    }
  }, [searchTerm, selectedLetter]);

  // Show error toast if there's an error
  useEffect(() => {
    if (groupsError) {
      toastError(t('organizations:directory.errorLoadingOrganizations', { message: groupsError.message }));
    }
  }, [groupsError, t]);

  // Show full skeleton on initial load, grid skeleton when filtering
  const shouldShowFullSkeleton = isLoadingGroups && isInitialLoad.current && !hasLoadedOnce.current;
  const shouldShowGridSkeleton = isLoadingGroups && !shouldShowFullSkeleton;

  const filteredOrganizations = useMemo(() => {
    if (!overrideOrganizations) {
      return organizations;
    }

    let filtered = organizations.filter((org) => {
      const matchesSearch = org.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesLetter =
        selectedLetter === '' ||
        (selectedLetter === '#'
          ? /^[0-9]/.test(org.name)
          : org.name.toLowerCase().startsWith(selectedLetter.toLowerCase()));

      return matchesSearch && matchesLetter;
    });

    return filtered;
  }, [organizations, overrideOrganizations, searchTerm, selectedLetter]);

  if (shouldShowFullSkeleton) {
    return <OrgDirectorySkeleton count={limit} />;
  }

  return (
    <div className="z-10 w-full px-4 md:px-8 md:mx-auto md:max-w-[1200px]">
      <div className="text-left mb-6 text-xs font-normal tracking-[0px]">
        {t('organizations:directory.organizationCount', { 
          filtered: filteredOrganizations.length, 
          total: totalOrgsCount 
        })}
      </div>

      <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      <DirectoryLetters
        selectedLetter={selectedLetter}
        onLetterSelect={setSelectedLetter}
        availableLetters={availableLetters}
      />

      {/* Show grid skeleton when filtering, otherwise show normal content */}
      {shouldShowGridSkeleton ? (
        <OrgDirectoryGridSkeleton count={limit} />
      ) : (
        <div
          className="md:grid gap-8 mb-8 w-full min-h-[400px] relative transition-all duration-300 ease-in-out"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 360px))',
            justifyContent: 'center'
          }}
        >
          {filteredOrganizations.length === 0 ? (
            <div className="col-span-full flex items-center justify-center py-8 md:py-12 px-4">
              <p className="text-gray-500 text-center text-sm md:text-base">
                {t('organizations:directory.noOrganizationsFound')}
              </p>
            </div>
          ) : (
            filteredOrganizations.map((org) => (
              <div 
                key={org.id}
                className="animate-in fade-in-0 duration-300"
              >
                <OrgCard
                  {...org}
                  associationId={associationId}
                  createdDate={datetime
                    .format(org.createdAt, 'MMM D, YYYY')
                    .toUpperCase()}
                  user={user}
                />
              </div>
            ))
          )}
        </div>
      )}

      {/* Load More button - always reserve space to prevent CLS */}
      <div className="text-center h-14 md:h-16 flex items-center justify-center px-4">
        {hasNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-4 md:px-6 py-2 md:py-3 bg-[#EFF2FF] rounded-full text-[#3853C4] font-semibold text-sm hover:bg-[#3853C4] hover:text-white active:scale-90 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-xs md:w-auto"
          >
            <span className="flex items-center justify-center gap-2">
              {isFetchingNextPage && (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {isFetchingNextPage ? t('organizations:directory.loading') : t('organizations:directory.loadMore')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OrgDirectory;
